import { z } from 'zod'

import {
  type Workspace,
  workspaceSchema,
  workspaceVersionSchema,
} from '@scalar-org/entities/workspace'

import type { RequestService } from '../request'

export function workspaceApiFactory(requestService: RequestService) {
  async function createWorkspace(
    namespace: string,
    data: Partial<
      Pick<
        Workspace,
        'tags' | 'title' | 'description' | 'isPrivate' | 'slug'
      > & {
        version: string
        loginPortal: string
        workspaceJson: string
        accessGroups: string[]
      }
    >,
  ) {
    return await requestService.request({
      url: `core/workspaces/${namespace}`,
      method: 'post',
      data,
      schema: workspaceSchema,
    })
  }

  async function updateWorkspace(
    namespace: string,
    slug: string,
    data: Partial<
      Pick<
        Workspace,
        | 'title'
        | 'description'
        | 'version'
        | 'namespace'
        | 'isPrivate'
        | 'tags'
        | 'slug'
      > & { loginPortal: string; accessGroups: string[] }
    >,
  ) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}`,
      method: 'patch',
      data,
      schema: workspaceSchema,
    })
  }

  async function deleteWorkspace(namespace: string, slug: string) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  async function deleteWorkspaceVersion(
    namespace: string,
    slug: string,
    version: string,
  ) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}/version/${version}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  async function versionWorkspace(
    namespace: string,
    slug: string,
    data: {
      version: string
      workspaceJson: string
      isCurrent?: boolean
    },
  ) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}/version`,
      method: 'post',
      data,
      schema: workspaceVersionSchema,
    })
  }

  async function addWorkspaceAccess(
    namespace: string,
    slug: string,
    group: string,
  ) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}/access/${group}`,
      method: 'post',
      schema: z.null(),
    })
  }

  async function removeWorkspaceAccess(
    namespace: string,
    slug: string,
    group: string,
  ) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}/access/${group}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  async function workspaceAvailability(namespace: string, slug: string) {
    return await requestService.request({
      url: `core/workspaces/${namespace}/${slug}/available`,
      method: 'get',
      schema: z.object({ available: z.boolean() }),
    })
  }

  return {
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    versionWorkspace,
    addWorkspaceAccess,
    removeWorkspaceAccess,
    workspaceAvailability,
    deleteWorkspaceVersion,
  }
}
